// src/components/asset/ModuleEditor.jsx
import React, { useState, useMemo } from 'react';
import {
  Box, Typography, Paper, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Button, Chip, Alert,
  TextField, InputAdornment, ToggleButton, ToggleButtonGroup, Badge
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon, 
  Extension as ModuleIcon, 
  Edit as EditIcon,
  Bolt as EnergyIcon,
  Rocket as PropulsionIcon,
  Security as WeaponIcon,
  Sensors as SensorIcon,
  Home as HabitationIcon,
  Inventory as CargoIcon,
  MedicalServices as MedicalIcon,
  Science as ResearchIcon,
  PrecisionManufacturing as ManufacturingIcon,
  Dock as DockingIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Agriculture as AgricultureIcon,
  Shield as ShieldIcon,
  Memory as MemoryIcon,
  Build as BuildIcon,
  Lan as LanIcon,
  Biotech as BiotechIcon,
  Settings as SettingsIcon,
  Construction as ConstructionIcon,
  Public as PublicIcon,
  RocketLaunch as RocketLaunchIcon
} from '@mui/icons-material';

const PURPOSE_CATEGORIES = {
  civilian: ['habitation', 'cargo', 'life_support', 'command', 'docking_bay', 'medical', 'agriculture', 'communication', 'navigation', 'structure'],
  military: ['weapon_system', 'shield_generator', 'armor'],
  industrial: ['energy', 'energy_storage', 'propulsion', 'manufacturing', 'mining', 'repair'],
  scientific: ['sensor', 'research', 'nanotech'],
  special: ['special', 'composite']
};

const PURPOSE_LABELS = {
  habitation: 'Жилой отсек',
  energy: 'Энергетика',
  energy_storage: 'Накопители',
  propulsion: 'Двигатели',
  weapon_system: 'Вооружение',
  shield_generator: 'Щиты',
  armor: 'Броня',
  sensor: 'Сенсоры',
  cargo: 'Груз',
  life_support: 'Жизнеобесп.',
  command: 'Управление',
  docking_bay: 'Стыковка',
  research: 'Лаборатория',
  medical: 'Медблок',
  manufacturing: 'Производство',
  mining: 'Добыча',
  agriculture: 'Ферма',
  communication: 'Связь',
  navigation: 'Навигация',
  special: 'Спец.',
  composite: 'Композит',
  nanotech: 'Нано',
  repair: 'Ремонт',
  structure: 'Структура'
};

const PURPOSE_ICONS = {
  propulsion: <RocketLaunchIcon />,
  weapon_system: <WeaponIcon />,
  shield_generator: <ShieldIcon />,
  armor: <ShieldIcon />,
  energy: <EnergyIcon />,
  energy_storage: <EnergyIcon />,
  sensor: <SensorIcon />,
  habitation: <HabitationIcon />,
  cargo: <CargoIcon />,
  medical: <MedicalIcon />,
  research: <ResearchIcon />,
  manufacturing: <ManufacturingIcon />,
  docking_bay: <DockingIcon />,
  mining: <ConstructionIcon />,
  agriculture: <AgricultureIcon />,
  communication: <LanIcon />,
  navigation: <PublicIcon />,
  repair: <BuildIcon />,
  nanotech: <BiotechIcon />,
  special: <SettingsIcon />,
  composite: <SettingsIcon />,
  structure: <MemoryIcon />,
  default: <ModuleIcon />
};

// Маппинг сред окружения
const ENVIRONMENT_ICONS = {
  vacuum: '🌌',
  atmosphere: '🌍',
  underwater: '🌊',
  surface: '🏔️',
  any: '🌌'
};

const ENVIRONMENT_LABELS = {
  vacuum: 'Вакуум',
  atmosphere: 'Атмосфера',
  underwater: 'Подводный',
  surface: 'Поверхность',
  any: 'Любая'
};

const ModuleEditor = ({ 
  modules, 
  onAddModule, 
  onCreateCustom, 
  onEditModule,
  onRemoveModule, 
  canEdit, 
  isDockMode,
  moduleLibrary,
  onAddFromLibrary,
  maxSlots,
  usedSlots
}) => {
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterPurpose, setFilterPurpose] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const purposeColors = {
    propulsion: '#ff9800', 
    energy: '#fdd835', 
    energy_storage: '#ffb74d',
    life_support: '#4caf50',
    weapon_system: '#f44336', 
    shield_generator: '#2196f3',
    armor: '#9e9e9e',
    command: '#2196f3', 
    habitation: '#8bc34a',
    cargo: '#795548', 
    sensor: '#00bcd4', 
    docking_bay: '#03a9f4',
    research: '#9c27b0',
    medical: '#e91e63',
    manufacturing: '#607d8b',
    mining: '#8d6e63',
    agriculture: '#66bb6a',
    communication: '#26a69a',
    navigation: '#26c6da',
    special: '#7e57c2',
    composite: '#ab47bc',
    nanotech: '#7c4dff',
    repair: '#78909c',
    structure: '#b0bec5',
    default: '#9e9e9e'
  };

  // Фильтрация модулей
  const filteredModules = useMemo(() => {
    if (!modules || !Array.isArray(modules)) return [];
    
    return modules.filter(module => {
      // Фильтр по категории
      if (filterCategory !== 'all') {
        const modulePurpose = module.purpose;
        const category = Object.entries(PURPOSE_CATEGORIES).find(([_, purposes]) => 
          purposes.includes(modulePurpose)
        )?.[0];
        
        if (category !== filterCategory) return false;
      }
      
      // Фильтр по конкретному назначению
      if (filterPurpose !== 'all' && module.purpose !== filterPurpose) {
        return false;
      }
      
      // Поиск по названию, описанию и тегам
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        return (
          (module.name && module.name.toLowerCase().includes(term)) ||
          (module.description && module.description.toLowerCase().includes(term)) ||
          (module.purpose && module.purpose.toLowerCase().includes(term)) ||
          (module.tags && Array.isArray(module.tags) && module.tags.some(tag => tag && tag.toLowerCase().includes(term)))
        );
      }
      
      return true;
    });
  }, [modules, filterCategory, filterPurpose, searchTerm]);

  const getModuleIcon = (purpose) => {
    return PURPOSE_ICONS[purpose] || PURPOSE_ICONS.default;
  };

  const getPowerColor = (draw) => {
    if (draw > 1000) return '#f44336';
    if (draw > 500) return '#ff9800';
    return '#4caf50';
  };

  const getPurposeLabel = (purpose) => {
    return PURPOSE_LABELS[purpose] || purpose;
  };

  const clearFilters = () => {
    setFilterCategory('all');
    setFilterPurpose('all');
    setSearchTerm('');
  };

  const activeFiltersCount = (filterCategory !== 'all' ? 1 : 0) + 
                            (filterPurpose !== 'all' ? 1 : 0) + 
                            (searchTerm ? 1 : 0);

  // Проверяем, что modules - массив
  const modulesArray = Array.isArray(modules) ? modules : [];
  const filteredArray = Array.isArray(filteredModules) ? filteredModules : [];

  // Получаем уникальные назначения для фильтра
  const uniquePurposes = useMemo(() => {
    const purposes = new Set();
    modulesArray.forEach(module => {
      if (module.purpose) purposes.add(module.purpose);
    });
    return Array.from(purposes).sort();
  }, [modulesArray]);

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ModuleIcon /> Модули ({filteredArray.length || 0})
          {modulesArray.length > 0 && modulesArray.length !== filteredArray.length && (
            <Chip 
              label={`${filteredArray.length}/${modulesArray.length}`} 
              size="small" 
              color="info"
              variant="outlined"
            />
          )}
        </Typography>
        
        {maxSlots !== undefined && (
          <Chip 
            label={`Слоты: ${usedSlots || 0}/${maxSlots}`}
            color={usedSlots >= maxSlots ? 'error' : usedSlots > maxSlots * 0.8 ? 'warning' : 'default'}
            size="small"
          />
        )}
        
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              size="small" 
              variant="outlined" 
              startIcon={<AddIcon />} 
              onClick={onAddModule}
              disabled={usedSlots >= maxSlots}
            >
              Из онтологии
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              color="secondary"
              startIcon={<EditIcon />} 
              onClick={onCreateCustom}
              disabled={usedSlots >= maxSlots}
            >
              Свой модуль
            </Button>
          </Box>
        )}
      </Box>

      {/* Панель фильтрации */}
      {modulesArray.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            startIcon={<FilterIcon />}
            endIcon={<Badge badgeContent={activeFiltersCount} color="primary" sx={{ '& .MuiBadge-badge': { right: -3, top: 3 } }} />}
            onClick={() => setShowFilters(!showFilters)}
            variant={showFilters ? 'contained' : 'text'}
            fullWidth
            sx={{ justifyContent: 'space-between', mb: 1 }}
          >
            Фильтры
          </Button>
          
          {showFilters && (
            <Box sx={{ p: 1, bgcolor: 'background.default', borderRadius: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Поиск по названию, описанию..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 1 }}
              />
              
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <ToggleButtonGroup
                  value={filterCategory}
                  exclusive
                  onChange={(e, val) => setFilterCategory(val || 'all')}
                  size="small"
                  sx={{ flex: 1 }}
                >
                  <ToggleButton value="all">Все</ToggleButton>
                  <ToggleButton value="civilian">🚀 Гражд.</ToggleButton>
                  <ToggleButton value="military">⚔️ Воен.</ToggleButton>
                  <ToggleButton value="industrial">🏭 Пром.</ToggleButton>
                  <ToggleButton value="scientific">🔬 Науч.</ToggleButton>
                </ToggleButtonGroup>
              </Box>
              
              {uniquePurposes.length > 0 && (
                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                  <InputLabel>Тип модуля</InputLabel>
                  <Select
                    value={filterPurpose}
                    label="Тип модуля"
                    onChange={(e) => setFilterPurpose(e.target.value)}
                  >
                    <MenuItem value="all">Все типы</MenuItem>
                    {uniquePurposes.map(purpose => (
                      <MenuItem key={purpose} value={purpose}>
                        {getPurposeLabel(purpose)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {activeFiltersCount > 0 && (
                <Button 
                  size="small" 
                  onClick={clearFilters}
                  sx={{ mt: 1 }}
                  fullWidth
                >
                  Сбросить фильтры
                </Button>
              )}
            </Box>
          )}
        </Box>
      )}

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {modulesArray.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>
            Нет установленных модулей. Добавьте модули из онтологии или создайте свой.
          </Alert>
        ) : filteredArray.length === 0 ? (
          <Alert severity="warning" sx={{ m: 1 }}>
            Нет модулей, соответствующих фильтрам
          </Alert>
        ) : (
          <List disablePadding>
            {filteredArray.map((mod, idx) => {
              const color = purposeColors[mod.purpose] || purposeColors.default;
              const isCustom = mod.is_custom;
              const powerDraw = mod.power_draw || mod.power_requirement || mod.base_stats?.power_draw || 0;
              const powerOutput = mod.power_output || mod.base_stats?.power_output || 0;
              const mass = mod.mass || mod.mass_kg || mod.base_stats?.mass_kg || 0;
              const crew = mod.crew_capacity || mod.base_stats?.crew_capacity || 0;
              const techLevel = mod.tech_level || mod.tech_level_min || 5;
              const slotSize = mod.slot_size || '3x';
              
              // Получаем среду окружения
              const environment = mod.environment_required || 
                                 (mod.compatible_environments && mod.compatible_environments[0]) || 
                                 'any';
              
              return (
                <ListItem 
                  key={mod.id || idx} 
                  onClick={() => canEdit && onEditModule && onEditModule(mod)}
                  sx={{ 
                    mb: 1, 
                    borderLeft: `4px solid ${color}`,
                    bgcolor: isCustom ? 'action.hover' : 'background.default',
                    borderRadius: 1,
                    cursor: canEdit && onEditModule ? 'pointer' : 'default',
                    '&:hover': canEdit && onEditModule ? { bgcolor: 'action.selected' } : {}
                  }}
                  secondaryAction={
                    canEdit && onEditModule && onRemoveModule ? (
                      <ListItemSecondaryAction>
                        <IconButton 
                          edge="end" 
                          size="small" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            onEditModule(mod);
                          }} 
                          color="primary"
                          sx={{ mr: 1 }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          edge="end" 
                          size="small" 
                          onClick={(e) => { 
                            e.stopPropagation(); 
                            onRemoveModule(mod.id); 
                          }} 
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    ) : null
                  }
                >
                  <Box sx={{ mr: 2, fontSize: '1.5rem', color: color }}>
                    {getModuleIcon(mod.purpose)}
                  </Box>
                  
                  <ListItemText 
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight="bold">
                          {mod.name || mod.type || mod.purpose}
                        </Typography>
                        {isCustom && (
                          <Chip 
                            label="Custom" 
                            size="small" 
                            color="secondary" 
                            variant="outlined" 
                            sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }} 
                          />
                        )}
                        <Chip 
                          label={getPurposeLabel(mod.purpose)} 
                          size="small" 
                          sx={{ 
                            height: 20, 
                            bgcolor: color + '20',
                            color: color,
                            '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                          }} 
                        />
                        <Chip 
                          label={slotSize} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 20, '& .MuiChip-label': { px: 1, fontSize: '0.7rem' } }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>🔧</span> T{techLevel}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>⚖️</span> {(mass / 1000).toFixed(1)}т
                        </Typography>
                        {powerOutput > 0 ? (
                          <Typography variant="caption" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>⚡</span> +{powerOutput} Вт
                          </Typography>
                        ) : powerDraw > 0 ? (
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: getPowerColor(powerDraw) }}>
                            <span>⚡</span> {powerDraw} Вт
                          </Typography>
                        ) : null}
                        {crew > 0 && (
                          <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>👥</span> {crew}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{ENVIRONMENT_ICONS[environment] || '🌌'}</span>
                          {ENVIRONMENT_LABELS[environment] || environment}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
      
      {isDockMode && canEdit && (
        <Alert severity="warning" sx={{ mt: 2, fontSize: '0.8rem' }}>
          Изменение модулей активного объекта может потребовать перезагрузки систем.
        </Alert>
      )}
    </Paper>
  );
};

export default ModuleEditor;